import { apiClient } from './api'
import type {
  ContentItem,
  EventForm,
  EventInfo,
  Session,
  Speaker,
} from '../data/types'

/**
 * Admin (write) API client. Calls the secured /api/admin endpoints; the auth
 * interceptor (see auth/AuthProvider) attaches the Entra bearer token.
 * Payloads are camelCase to match the backend admin schemas.
 */

export interface EventSummary {
  id: string
  name: string
  slug: string
  tagline?: string
  status: string
  startsAt: string
  endsAt: string
  timezone: string
  location?: string
  templateName?: string
  templateDescription?: string
  isStarter?: boolean
}

export interface EventWritePayload {
  name: string
  slug: string
  tagline?: string
  startsAt: string
  endsAt: string
  timezone?: string
  location?: string
  venueName?: string
  venueAddress?: string
  venueCity?: string
  venueMapUrl?: string
  welcomeMessage?: string
  dressCode?: string
  travel?: string
  heroImage?: string | null
  primaryColor?: string
  accentColor?: string
  logoUrl?: string | null
  loginBackgroundUrl?: string | null
  emailFromName?: string | null
  emailFromAddress?: string | null
  faqs?: { question: string; answer: string }[]
  contacts?: { name: string; role?: string; email: string; phone?: string }[]
  travelVariants?: TravelVariantWrite[]
  registrationMode?: string // invite_only/public
  registrationOpen?: boolean
  registrationCapacity?: number | null
  registrationClosesAt?: string | null
  registrationCode?: string | null // shared invitation code (null = none)
  agendaStatus?: string // published/draft
}

export interface TravelVariantWrite {
  label?: string
  body: string
  targetingRuleId?: string
}

export interface SpeakerWritePayload {
  name: string
  title?: string
  company?: string
  bio?: string
  avatarColor?: string
  initials?: string
  photoUrl?: string
  featured?: boolean
}

export interface SessionWritePayload {
  title: string
  description?: string
  startsAt: string
  endsAt: string
  location?: string
  track?: string
  type: string
  recordingUrl?: string | null
  speakerIds: string[]
  targetingRuleId?: string | null
}

export interface ContentWritePayload {
  title: string
  description?: string
  category?: string
  kind: string
  url?: string
  sizeLabel?: string
  availableFrom?: string
  ordinal?: number
  targetingRuleId?: string | null
}

export interface FormFieldPayload {
  label: string
  type: string
  required?: boolean
  options?: string[]
  helperText?: string
}

export interface FormWritePayload {
  title: string
  description?: string
  type: string
  status: string
  dueLabel?: string
  ordinal?: number
  targetingRuleId?: string | null
  fields: FormFieldPayload[]
}

const base = '/admin'

/** Build the from/to query params for analytics calls, omitting empty bounds. */
function rangeParams(range?: AnalyticsRange): Record<string, string> | undefined {
  if (!range) return undefined
  const params: Record<string, string> = {}
  if (range.from) params.from = range.from
  if (range.to) params.to = range.to
  return Object.keys(params).length ? params : undefined
}

export const adminService = {
  // Events
  // Global platform settings — email sender (from name/address).
  getEmailSender: () =>
    apiClient
      .get<{ fromName: string; fromAddress: string; allowedDomains: string[] }>(
        `${base}/settings/email-sender`,
      )
      .then((r) => r.data),
  setEmailSender: (payload: { fromName: string; fromAddress: string }) =>
    apiClient
      .patch<{ fromName: string; fromAddress: string }>(
        `${base}/settings/email-sender`,
        payload,
      )
      .then((r) => r.data),

  listEvents: () =>
    apiClient.get<EventSummary[]>(`${base}/events`).then((r) => r.data),
  getEvent: (id: string) =>
    apiClient.get<EventInfo>(`${base}/events/${id}`).then((r) => r.data),
  createEvent: (payload: EventWritePayload) =>
    apiClient.post<EventInfo>(`${base}/events`, payload).then((r) => r.data),
  updateEvent: (id: string, payload: Partial<EventWritePayload> & { status?: string }) =>
    apiClient.patch<EventInfo>(`${base}/events/${id}`, payload).then((r) => r.data),
  publishEvent: (id: string) =>
    apiClient.post<EventInfo>(`${base}/events/${id}/publish`).then((r) => r.data),
  unpublishEvent: (id: string) =>
    apiClient.post<EventInfo>(`${base}/events/${id}/unpublish`).then((r) => r.data),
  cloneEvent: (id: string) =>
    apiClient.post<EventInfo>(`${base}/events/${id}/clone`).then((r) => r.data),
  deleteEvent: (id: string) => apiClient.delete(`${base}/events/${id}`),

  // Templates (Event rows with status="template")
  listTemplates: () =>
    apiClient.get<EventSummary[]>(`${base}/templates`).then((r) => r.data),
  saveAsTemplate: (eventId: string, payload: { name: string; description?: string }) =>
    apiClient
      .post<EventSummary>(`${base}/events/${eventId}/save-as-template`, payload)
      .then((r) => r.data),
  createFromTemplate: (templateId: string) =>
    apiClient
      .post<EventInfo>(`${base}/templates/${templateId}/create-event`)
      .then((r) => r.data),
  deleteTemplate: (templateId: string) =>
    apiClient.delete(`${base}/templates/${templateId}`),

  // Audit log
  listAudit: (eventId?: string) =>
    apiClient
      .get<{ total: number; entries: AuditEntry[] }>(`${base}/audit-log`, {
        params: eventId ? { eventId } : undefined,
      })
      .then((r) => r.data),

  // Notifications
  sendNotification: (
    eventId: string,
    payload: { title: string; body?: string; url?: string },
  ) =>
    apiClient
      .post<{ id: string; title: string; delivered: number; subscribers: number }>(
        `${base}/events/${eventId}/notifications`,
        payload,
      )
      .then((r) => r.data),
  listNotifications: (eventId: string) =>
    apiClient
      .get<{ notifications: NotificationRow[] }>(
        `${base}/events/${eventId}/notifications`,
      )
      .then((r) => r.data),

  // Assets (media upload)
  uploadAsset: (eventId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return apiClient
      .post<{
        id: string
        url: string
        filename: string
        contentType: string
        byteSize: number
      }>(
        // No manual Content-Type: the browser sets multipart/form-data with the
        // required boundary for FormData bodies.
        `${base}/events/${eventId}/assets`,
        fd,
      )
      .then((r) => r.data)
  },
  deleteAsset: (eventId: string, assetId: string) =>
    apiClient.delete(`${base}/events/${eventId}/assets/${assetId}`),
  /** Upload a session recording video (larger size cap, video content-types).
   *  Returns the served /api/assets/{id} URL to store as a session recordingUrl. */
  uploadRecording: (eventId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return apiClient
      .post<{
        id: string
        url: string
        filename: string
        contentType: string
        byteSize: number
      }>(
        // No manual Content-Type: the browser sets multipart/form-data with the
        // required boundary for FormData bodies.
        `${base}/events/${eventId}/recordings`,
        fd,
      )
      .then((r) => r.data)
  },

  // Speakers
  createSpeaker: (eventId: string, payload: SpeakerWritePayload) =>
    apiClient
      .post<Speaker>(`${base}/events/${eventId}/speakers`, payload)
      .then((r) => r.data),
  updateSpeaker: (eventId: string, id: string, payload: Partial<SpeakerWritePayload>) =>
    apiClient
      .patch<Speaker>(`${base}/events/${eventId}/speakers/${id}`, payload)
      .then((r) => r.data),
  deleteSpeaker: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/speakers/${id}`),

  // Sessions
  createSession: (eventId: string, payload: SessionWritePayload) =>
    apiClient
      .post<Session>(`${base}/events/${eventId}/sessions`, payload)
      .then((r) => r.data),
  updateSession: (eventId: string, id: string, payload: Partial<SessionWritePayload>) =>
    apiClient
      .patch<Session>(`${base}/events/${eventId}/sessions/${id}`, payload)
      .then((r) => r.data),
  deleteSession: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/sessions/${id}`),

  // Content
  createContent: (eventId: string, payload: ContentWritePayload) =>
    apiClient
      .post<ContentItem>(`${base}/events/${eventId}/content`, payload)
      .then((r) => r.data),
  updateContent: (eventId: string, id: string, payload: Partial<ContentWritePayload>) =>
    apiClient
      .patch<ContentItem>(`${base}/events/${eventId}/content/${id}`, payload)
      .then((r) => r.data),
  deleteContent: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/content/${id}`),

  // Forms
  createForm: (eventId: string, payload: FormWritePayload) =>
    apiClient
      .post<EventForm>(`${base}/events/${eventId}/forms`, payload)
      .then((r) => r.data),
  updateForm: (eventId: string, id: string, payload: Partial<FormWritePayload>) =>
    apiClient
      .patch<EventForm>(`${base}/events/${eventId}/forms/${id}`, payload)
      .then((r) => r.data),
  deleteForm: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/forms/${id}`),

  // Sub-resource reads (reuse the event read endpoints for editing views)
  listSessions: (eventId: string) =>
    apiClient.get<Session[]>(`/events/${eventId}/sessions`).then((r) => r.data),
  listSpeakers: (eventId: string) =>
    apiClient.get<Speaker[]>(`/events/${eventId}/speakers`).then((r) => r.data),
  listContent: (eventId: string) =>
    apiClient.get<ContentItem[]>(`/events/${eventId}/content`).then((r) => r.data),
  listForms: (eventId: string) =>
    apiClient
      .get<EventForm[]>(`${base}/events/${eventId}/forms`)
      .then((r) => r.data),

  // Form responses (read)
  listResponses: (eventId: string, formId: string) =>
    apiClient
      .get<FormResponsesResult>(`${base}/events/${eventId}/forms/${formId}/responses`)
      .then((r) => r.data),

  // CSV export (returns a Blob for client-side download)
  exportResponses: (eventId: string, formId: string) =>
    apiClient
      .get<Blob>(`${base}/events/${eventId}/forms/${formId}/responses/export`, {
        responseType: 'blob',
      })
      .then((r) => r.data),

  // Targeting rules
  listRules: (eventId: string) =>
    apiClient
      .get<TargetingRule[]>(`${base}/events/${eventId}/targeting-rules`)
      .then((r) => r.data),
  createRule: (eventId: string, payload: TargetingRuleWrite) =>
    apiClient
      .post<TargetingRule>(`${base}/events/${eventId}/targeting-rules`, payload)
      .then((r) => r.data),
  updateRule: (eventId: string, id: string, payload: TargetingRuleWrite) =>
    apiClient
      .patch<TargetingRule>(`${base}/events/${eventId}/targeting-rules/${id}`, payload)
      .then((r) => r.data),
  deleteRule: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/targeting-rules/${id}`),
  targetingKeys: (eventId: string) =>
    apiClient
      .get<{ keys: { key: string; values: string[] }[] }>(
        `${base}/events/${eventId}/targeting-keys`,
      )
      .then((r) => r.data),

  // Reporting
  eventSummary: (eventId: string) =>
    apiClient
      .get<EventReport>(`${base}/reports/events/${eventId}/summary`)
      .then((r) => r.data),
  analytics: (eventId: string, range?: AnalyticsRange) =>
    apiClient
      .get<Analytics>(`${base}/reports/events/${eventId}/analytics`, {
        params: rangeParams(range),
      })
      .then((r) => r.data),
  analyticsStage: (eventId: string, stage: string, range?: AnalyticsRange) =>
    apiClient
      .get<AnalyticsStageAttendee[]>(
        `${base}/reports/events/${eventId}/analytics/stage/${stage}`,
        { params: rangeParams(range) },
      )
      .then((r) => r.data),
  exportAnalytics: (eventId: string, range?: AnalyticsRange) =>
    apiClient
      .get<Blob>(`${base}/reports/events/${eventId}/analytics/export`, {
        responseType: 'blob',
        params: rangeParams(range),
      })
      .then((r) => r.data),
  exportEngagement: (eventId: string, range?: AnalyticsRange) =>
    apiClient
      .get<Blob>(`${base}/reports/events/${eventId}/analytics/export/engagement`, {
        responseType: 'blob',
        params: rangeParams(range),
      })
      .then((r) => r.data),

  // Attendees
  listAttendees: (eventId: string) =>
    apiClient
      .get<AttendeeRow[]>(`${base}/events/${eventId}/attendees`)
      .then((r) => r.data),
  // Full attendee roster: every attendee + selections + form answers (dynamic
  // columns). Powers the on-screen roster table.
  getAttendeeRoster: (eventId: string) =>
    apiClient
      .get<AttendeeRoster>(`${base}/events/${eventId}/attendees/roster`)
      .then((r) => r.data),
  exportAttendeeRoster: (eventId: string) =>
    apiClient
      .get<Blob>(`${base}/events/${eventId}/attendees/roster/export`, {
        responseType: 'blob',
      })
      .then((r) => r.data),
  createAttendee: (eventId: string, payload: AttendeeWritePayload) =>
    apiClient
      .post<AttendeeRow>(`${base}/events/${eventId}/attendees`, payload)
      .then((r) => r.data),
  importAttendees: (eventId: string, attendees: AttendeeWritePayload[]) =>
    apiClient
      .post<{ created: number; updated: number; total: number }>(
        `${base}/events/${eventId}/attendees/import`,
        { attendees },
      )
      .then((r) => r.data),
  updateAttendee: (eventId: string, id: string, payload: AttendeeUpdatePayload) =>
    apiClient
      .patch<AttendeeRow>(`${base}/events/${eventId}/attendees/${id}`, payload)
      .then((r) => r.data),
  bulkUpdateAttendees: (eventId: string, payload: AttendeeBulkPayload) =>
    apiClient
      .post<{ updated: number }>(
        `${base}/events/${eventId}/attendees/bulk`,
        payload,
      )
      .then((r) => r.data),
  deleteAttendee: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/attendees/${id}`),
  // Special-guest travel & lodging.
  getGuestTravel: (eventId: string, attendeeId: string) =>
    apiClient
      .get<GuestTravel>(`${base}/events/${eventId}/attendees/${attendeeId}/travel`)
      .then((r) => r.data),
  saveGuestTravel: (eventId: string, attendeeId: string, payload: GuestTravel) =>
    apiClient
      .put<GuestTravel>(
        `${base}/events/${eventId}/attendees/${attendeeId}/travel`,
        payload,
      )
      .then((r) => r.data),
  sendGuestTravelEmail: (eventId: string, attendeeId: string) =>
    apiClient
      .post<{ status: string; to: string }>(
        `${base}/events/${eventId}/attendees/${attendeeId}/travel/send`,
        {},
      )
      .then((r) => r.data),
  inviteAttendee: (eventId: string, id: string) =>
    apiClient
      .post<{ status: string; email: string }>(
        `${base}/events/${eventId}/attendees/${id}/invite`,
      )
      .then((r) => r.data),
  // Registration invites: a direct sign-in + register link (longer-lived).
  registrationInviteAttendee: (eventId: string, id: string) =>
    apiClient
      .post<{ status: string; email: string }>(
        `${base}/events/${eventId}/attendees/${id}/registration-invite`,
      )
      .then((r) => r.data),
  registrationInviteAll: (eventId: string) =>
    apiClient
      .post<{ status: string; count: number; total: number }>(
        `${base}/events/${eventId}/attendees/registration-invite`,
      )
      .then((r) => r.data),

  // --- Page & email composition (block builder) ---
  listPages: (eventId: string) =>
    apiClient
      .get<AdminPageSummary[]>(`${base}/events/${eventId}/pages`)
      .then((r) => r.data),
  getPage: (eventId: string, pageId: string) =>
    apiClient
      .get<AdminPage>(`${base}/events/${eventId}/pages/${pageId}`)
      .then((r) => r.data),
  createPage: (eventId: string, payload: PageWrite) =>
    apiClient
      .post<AdminPage>(`${base}/events/${eventId}/pages`, payload)
      .then((r) => r.data),
  updatePage: (eventId: string, pageId: string, payload: PageWrite) =>
    apiClient
      .patch<AdminPage>(`${base}/events/${eventId}/pages/${pageId}`, payload)
      .then((r) => r.data),
  deletePage: (eventId: string, pageId: string) =>
    apiClient.delete(`${base}/events/${eventId}/pages/${pageId}`),
  reorderPages: (eventId: string, ids: string[]) =>
    apiClient.post(`${base}/events/${eventId}/pages/reorder`, { ids }),
  // Built-in nav item visibility/order.
  getNavSettings: (eventId: string) =>
    apiClient
      .get<{ settings: NavSettingRow[] }>(`${base}/events/${eventId}/nav-settings`)
      .then((r) => r.data.settings),
  updateNavSettings: (eventId: string, settings: NavSettingRow[]) =>
    apiClient.put(`${base}/events/${eventId}/nav-settings`, { settings }),
  createBlock: (eventId: string, pageId: string, payload: BlockWrite) =>
    apiClient
      .post<AdminBlock>(`${base}/events/${eventId}/pages/${pageId}/blocks`, payload)
      .then((r) => r.data),
  updateBlock: (eventId: string, blockId: string, payload: BlockWrite) =>
    apiClient
      .patch<AdminBlock>(`${base}/events/${eventId}/blocks/${blockId}`, payload)
      .then((r) => r.data),
  deleteBlock: (eventId: string, blockId: string) =>
    apiClient.delete(`${base}/events/${eventId}/blocks/${blockId}`),
  duplicateBlock: (eventId: string, blockId: string) =>
    apiClient
      .post<AdminBlock>(`${base}/events/${eventId}/blocks/${blockId}/duplicate`)
      .then((r) => r.data),
  duplicatePage: (eventId: string, pageId: string) =>
    apiClient
      .post<AdminPage>(`${base}/events/${eventId}/pages/${pageId}/duplicate`)
      .then((r) => r.data),
  reorderBlocks: (eventId: string, pageId: string, ids: string[]) =>
    apiClient.post(`${base}/events/${eventId}/pages/${pageId}/blocks/reorder`, {
      ids,
    }),

  // Page/section template library (cross-event reuse)
  listPageTemplates: () =>
    apiClient.get<PageTemplate[]>(`${base}/page-templates`).then((r) => r.data),
  savePageTemplate: (
    eventId: string,
    pageId: string,
    payload: { name: string; description?: string; blockId?: string },
  ) =>
    apiClient
      .post<PageTemplate>(
        `${base}/events/${eventId}/pages/${pageId}/save-template`,
        payload,
      )
      .then((r) => r.data),
  insertPageTemplate: (eventId: string, pageId: string, templateId: string) =>
    apiClient
      .post<AdminPage>(
        `${base}/events/${eventId}/pages/${pageId}/insert-template`,
        { templateId },
      )
      .then((r) => r.data),
  deletePageTemplate: (templateId: string) =>
    apiClient.delete(`${base}/page-templates/${templateId}`),

  // Email templates (per-event overrides)
  listEmailTemplates: (eventId: string) =>
    apiClient
      .get<AdminEmailTemplate[]>(`${base}/events/${eventId}/email-templates`)
      .then((r) => r.data),
  createEmailTemplate: (eventId: string, kind: string) =>
    apiClient
      .post<AdminEmailTemplate>(`${base}/events/${eventId}/email-templates`, {
        kind,
      })
      .then((r) => r.data),
  updateEmailTemplate: (
    eventId: string,
    tplId: string,
    payload: { subject?: string; enabled?: boolean },
  ) =>
    apiClient
      .patch<AdminEmailTemplate>(
        `${base}/events/${eventId}/email-templates/${tplId}`,
        payload,
      )
      .then((r) => r.data),
  deleteEmailTemplate: (eventId: string, tplId: string) =>
    apiClient.delete(`${base}/events/${eventId}/email-templates/${tplId}`),
  createEmailBlock: (eventId: string, tplId: string, payload: BlockWrite) =>
    apiClient
      .post<AdminBlock>(
        `${base}/events/${eventId}/email-templates/${tplId}/blocks`,
        payload,
      )
      .then((r) => r.data),
  reorderEmailBlocks: (eventId: string, tplId: string, ids: string[]) =>
    apiClient.post(
      `${base}/events/${eventId}/email-templates/${tplId}/blocks/reorder`,
      { ids },
    ),
  testSendEmailTemplate: (eventId: string, tplId: string, to: string) =>
    apiClient
      .post<{ status: string; to: string }>(
        `${base}/events/${eventId}/email-templates/${tplId}/test-send`,
        { to },
      )
      .then((r) => r.data),
  previewEmailTemplate: (eventId: string, tplId: string, attendeeId?: string) =>
    apiClient
      .post<{ subject: string; html: string }>(
        `${base}/events/${eventId}/email-templates/${tplId}/preview`,
        attendeeId ? { attendeeId } : {},
      )
      .then((r) => r.data),

  // Outreach — scheduled surveys + email campaigns
  listSurveySchedules: (eventId: string) =>
    apiClient.get<SurveySchedule[]>(`${base}/events/${eventId}/survey-schedules`).then((r) => r.data),
  createSurveySchedule: (eventId: string, payload: SurveyScheduleWrite) =>
    apiClient.post<SurveySchedule>(`${base}/events/${eventId}/survey-schedules`, payload).then((r) => r.data),
  deleteSurveySchedule: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/survey-schedules/${id}`),
  listCampaigns: (eventId: string) =>
    apiClient.get<Campaign[]>(`${base}/events/${eventId}/campaigns`).then((r) => r.data),
  createCampaign: (eventId: string, payload: CampaignWrite) =>
    apiClient.post<Campaign>(`${base}/events/${eventId}/campaigns`, payload).then((r) => r.data),
  updateCampaign: (eventId: string, id: string, payload: CampaignWrite) =>
    apiClient.patch<Campaign>(`${base}/events/${eventId}/campaigns/${id}`, payload).then((r) => r.data),
  scheduleCampaign: (eventId: string, id: string, schedule: CampaignSchedule = {}) =>
    apiClient
      .post<Campaign>(`${base}/events/${eventId}/campaigns/${id}/schedule`, schedule)
      .then((r) => r.data),
  cancelCampaign: (eventId: string, id: string) =>
    apiClient.post<Campaign>(`${base}/events/${eventId}/campaigns/${id}/cancel`).then((r) => r.data),
  deleteCampaign: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/campaigns/${id}`),

  // Integrations — webhooks + API keys
  listWebhooks: (eventId: string) =>
    apiClient.get<WebhooksResponse>(`${base}/events/${eventId}/webhooks`).then((r) => r.data),
  createWebhook: (eventId: string, payload: { url: string; events?: string; description?: string }) =>
    apiClient.post<Webhook>(`${base}/events/${eventId}/webhooks`, payload).then((r) => r.data),
  updateWebhook: (eventId: string, id: string, payload: Partial<Webhook>) =>
    apiClient.patch<Webhook>(`${base}/events/${eventId}/webhooks/${id}`, payload).then((r) => r.data),
  deleteWebhook: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/webhooks/${id}`),
  testWebhook: (eventId: string, id: string) =>
    apiClient.post<{ status: string }>(`${base}/events/${eventId}/webhooks/${id}/test`).then((r) => r.data),
  listApiKeys: (eventId: string) =>
    apiClient.get<ApiKeyInfo[]>(`${base}/events/${eventId}/api-keys`).then((r) => r.data),
  createApiKey: (eventId: string, name: string) =>
    apiClient
      .post<{ id: string; name: string; prefix: string; key: string }>(
        `${base}/events/${eventId}/api-keys`,
        { name },
      )
      .then((r) => r.data),
  deleteApiKey: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/api-keys/${id}`),

  // Check-in
  checkinSummary: (eventId: string) =>
    apiClient.get<CheckinSummary>(`${base}/events/${eventId}/checkin/summary`).then((r) => r.data),
  scanCheckin: (eventId: string, token: string) =>
    apiClient
      .post<CheckinResult>(`${base}/events/${eventId}/checkin/scan`, { token })
      .then((r) => r.data),
  manualCheckin: (eventId: string, attendeeId: string, undo = false) =>
    apiClient
      .post<CheckinResult>(
        `${base}/events/${eventId}/attendees/${attendeeId}/checkin`,
        undefined,
        { params: { undo } },
      )
      .then((r) => r.data),

  // Sponsors & exhibitors
  listSponsors: (eventId: string) =>
    apiClient.get<AdminSponsor[]>(`${base}/events/${eventId}/sponsors`).then((r) => r.data),
  createSponsor: (eventId: string, payload: SponsorWrite) =>
    apiClient.post<AdminSponsor>(`${base}/events/${eventId}/sponsors`, payload).then((r) => r.data),
  updateSponsor: (eventId: string, sponsorId: string, payload: SponsorWrite) =>
    apiClient
      .patch<AdminSponsor>(`${base}/events/${eventId}/sponsors/${sponsorId}`, payload)
      .then((r) => r.data),
  deleteSponsor: (eventId: string, sponsorId: string) =>
    apiClient.delete(`${base}/events/${eventId}/sponsors/${sponsorId}`),
  exportSponsorLeads: (eventId: string) =>
    apiClient
      .get<Blob>(`${base}/events/${eventId}/sponsors/leads/export`, { responseType: 'blob' })
      .then((r) => r.data),

  // Live engagement — polls & Q&A
  listPolls: (eventId: string) =>
    apiClient.get<AdminPoll[]>(`${base}/events/${eventId}/polls`).then((r) => r.data),
  createPoll: (
    eventId: string,
    payload: { question: string; options: string[]; sessionIds: string[]; status?: string },
  ) =>
    apiClient.post<AdminPoll>(`${base}/events/${eventId}/polls`, payload).then((r) => r.data),
  updatePoll: (
    eventId: string,
    pollId: string,
    payload: { status?: string; question?: string; sessionIds?: string[] },
  ) =>
    apiClient
      .patch<AdminPoll>(`${base}/events/${eventId}/polls/${pollId}`, payload)
      .then((r) => r.data),
  deletePoll: (eventId: string, pollId: string) =>
    apiClient.delete(`${base}/events/${eventId}/polls/${pollId}`),
  listQuestions: (eventId: string) =>
    apiClient
      .get<AdminQuestion[]>(`${base}/events/${eventId}/questions`)
      .then((r) => r.data),
  updateQuestion: (
    eventId: string,
    qid: string,
    payload: { status?: string; answered?: boolean; answerBody?: string },
  ) =>
    apiClient
      .patch(`${base}/events/${eventId}/questions/${qid}`, payload)
      .then((r) => r.data),
  deleteQuestion: (eventId: string, qid: string) =>
    apiClient.delete(`${base}/events/${eventId}/questions/${qid}`),

  // Registration fields (admin-configured)
  listRegistrationFields: (eventId: string) =>
    apiClient
      .get<RegistrationField[]>(`${base}/events/${eventId}/registration-fields`)
      .then((r) => r.data),
  createRegistrationField: (eventId: string, payload: RegistrationFieldWrite) =>
    apiClient
      .post<RegistrationField>(
        `${base}/events/${eventId}/registration-fields`,
        payload,
      )
      .then((r) => r.data),
  updateRegistrationField: (
    eventId: string,
    id: string,
    payload: RegistrationFieldWrite,
  ) =>
    apiClient
      .patch<RegistrationField>(
        `${base}/events/${eventId}/registration-fields/${id}`,
        payload,
      )
      .then((r) => r.data),
  deleteRegistrationField: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/registration-fields/${id}`),

  // Option groups + options (e.g. dinners)
  listOptionGroups: (eventId: string) =>
    apiClient
      .get<OptionGroupAdmin[]>(`${base}/events/${eventId}/option-groups`)
      .then((r) => r.data),
  createOptionGroup: (eventId: string, payload: OptionGroupWrite) =>
    apiClient
      .post<OptionGroupAdmin>(`${base}/events/${eventId}/option-groups`, payload)
      .then((r) => r.data),
  updateOptionGroup: (eventId: string, id: string, payload: OptionGroupWrite) =>
    apiClient
      .patch<OptionGroupAdmin>(
        `${base}/events/${eventId}/option-groups/${id}`,
        payload,
      )
      .then((r) => r.data),
  deleteOptionGroup: (eventId: string, id: string) =>
    apiClient.delete(`${base}/events/${eventId}/option-groups/${id}`),
  createOption: (eventId: string, groupId: string, payload: OptionWrite) =>
    apiClient
      .post<OptionAdmin>(
        `${base}/events/${eventId}/option-groups/${groupId}/options`,
        payload,
      )
      .then((r) => r.data),
  updateOption: (eventId: string, optionId: string, payload: OptionWrite) =>
    apiClient
      .patch<OptionAdmin>(`${base}/events/${eventId}/options/${optionId}`, payload)
      .then((r) => r.data),
  deleteOption: (eventId: string, optionId: string) =>
    apiClient.delete(`${base}/events/${eventId}/options/${optionId}`),

  // Registrations + rosters + export + override
  listRegistrations: (eventId: string) =>
    apiClient
      .get<RegistrationRow[]>(`${base}/events/${eventId}/registrations`)
      .then((r) => r.data),
  exportRegistrations: (eventId: string) =>
    apiClient
      .get<Blob>(`${base}/events/${eventId}/registrations/export`, {
        responseType: 'blob',
      })
      .then((r) => r.data),

  // People / cross-event profiles
  listPersonProfiles: (q?: string) =>
    apiClient
      .get<PersonProfile[]>(`${base}/person-profiles`, { params: q ? { q } : {} })
      .then((r) => r.data),
  addPersonAlias: (personId: string, email: string) =>
    apiClient
      .post<PersonProfile>(`${base}/person-profiles/${personId}/aliases`, { email })
      .then((r) => r.data),
  removePersonAlias: (personId: string, aliasId: string) =>
    apiClient.delete(`${base}/person-profiles/${personId}/aliases/${aliasId}`),
  /** Delete a cross-event profile. The person re-creates it on next sign-in
   *  (onboarding gate re-triggers for every linked email/attendee). */
  deletePersonProfile: (personId: string) =>
    apiClient.delete(`${base}/person-profiles/${personId}`),

  // Public self-registration approval queue.
  listRegistrationApprovals: (eventId: string, status = 'pending_approval') =>
    apiClient
      .get<ApprovalRow[]>(`${base}/events/${eventId}/registration-approvals`, {
        params: { status_filter: status },
      })
      .then((r) => r.data),
  approveRegistration: (eventId: string, registrationId: string) =>
    apiClient
      .post(
        `${base}/events/${eventId}/registration-approvals/${registrationId}/approve`,
      )
      .then((r) => r.data),
  rejectRegistration: (eventId: string, registrationId: string) =>
    apiClient
      .post(
        `${base}/events/${eventId}/registration-approvals/${registrationId}/reject`,
      )
      .then((r) => r.data),
  getGroupRoster: (eventId: string, groupId: string) =>
    apiClient
      .get<GroupRoster>(
        `${base}/events/${eventId}/option-groups/${groupId}/roster`,
      )
      .then((r) => r.data),
  setAttendeeSelection: (
    eventId: string,
    attendeeId: string,
    groupId: string,
    optionId: string | null,
  ) =>
    apiClient
      .post(`${base}/events/${eventId}/attendees/${attendeeId}/selection`, {
        groupId,
        optionId,
      })
      .then((r) => r.data),

  // AI Concierge (admin) — per-event toggle + token/cost usage report.
  setConciergeConfig: (
    eventId: string,
    payload: { enabled?: boolean; budgetUsd?: number | null },
  ) =>
    apiClient
      .patch<{ enabled: boolean; budgetUsd: number | null; globalEnabled: boolean }>(
        `${base}/events/${eventId}/concierge`,
        payload,
      )
      .then((r) => r.data),
  getConciergeUsage: (eventId: string, range?: { from?: string; to?: string }) =>
    apiClient
      .get<ConciergeUsageReport>(`${base}/events/${eventId}/concierge/usage`, {
        params: range,
      })
      .then((r) => r.data),
  conciergeUsageCsvUrl: (eventId: string) =>
    `${apiClient.defaults.baseURL ?? ''}${base}/events/${eventId}/concierge/usage.csv`,
}

export interface ConciergeUsageReport {
  eventId: string
  globalEnabled: boolean
  eventEnabled: boolean
  totals: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
    requests: number
    estCostUsd: number
  }
  today: {
    date: string
    estCostUsd: number
    dailyBudgetUsd: number
    capReached: boolean
  }
  perDay: {
    date: string
    promptTokens: number
    completionTokens: number
    requests: number
    estCostUsd: number
  }[]
  perModel: {
    model: string
    promptTokens: number
    completionTokens: number
    requests: number
    estCostUsd: number
  }[]
  budget: {
    budgetUsd: number | null
    totalSpendUsd: number
    capReached: boolean
  }
}

export interface AttendeeWritePayload {
  email: string
  fullName?: string
  company?: string
  attendeeType?: string
  track?: string
  externalRef?: string
}

// --- Page & email composition (admin) ------------------------------------

export type AdminBlockType =
  | 'hero'
  | 'richtext'
  | 'html'
  | 'image'
  | 'button'
  | 'divider'
  | 'agenda'
  | 'speakers'
  | 'content'
  | 'columns'
  | 'embed'
  | 'sponsors'
  | 'eventinfo'
  | 'travel'

export interface AdminBlock {
  id: string
  blockType: AdminBlockType
  ordinal: number
  config: Record<string, unknown>
  html?: string | null
  source?: string | null
  children?: AdminBlock[]
}

export interface BlockWrite {
  blockType: AdminBlockType
  config?: Record<string, unknown>
  source?: string | null
  parentBlockId?: string | null
  position?: number | null
}

export type NavVisibility = 'auto' | 'show' | 'hide'
export interface NavSettingRow {
  navKey: string
  visibility: NavVisibility
  ordinal: number
}

export interface AdminPageSummary {
  id: string
  slug: string
  title: string
  showInNav: boolean
  navLabel?: string | null
  ordinal: number
  isHome: boolean
  published: boolean
  targetingRuleId?: string | null
}

export interface AdminPage extends AdminPageSummary {
  blocks: AdminBlock[]
}

export interface PageWrite {
  slug?: string
  title?: string
  showInNav?: boolean
  navLabel?: string | null
  ordinal?: number
  published?: boolean
  targetingRuleId?: string | null
}

export interface AdminEmailTemplate {
  id: string
  kind: string
  subject?: string | null
  enabled: boolean
  blocks: AdminBlock[]
}

export interface SurveySchedule {
  id: string
  formId: string
  trigger: string
  sessionId?: string | null
  offsetMinutes: number
  fixedAt?: string | null
  status: string
  dueAt?: string | null
}
export interface SurveyScheduleWrite {
  formId: string
  trigger: string
  sessionId?: string | null
  offsetMinutes?: number
  fixedAt?: string | null
}
export interface Campaign {
  id: string
  name: string
  subject: string
  bodyHtml: string
  bodySource?: string | null
  targetingRuleId?: string | null
  status: string
  scheduledAt?: string | null
  sentAt?: string | null
  sentCount?: number
  openCount?: number
  scheduleRelativeTo?: string | null // event_start/event_end
  scheduleOffsetMinutes?: number | null // signed; negative = before
}

/** Campaign schedule request. Empty = send now; sendAt = absolute; relativeTo +
 *  offsetMinutes = relative to the event (offset negative = before). */
export interface CampaignSchedule {
  sendAt?: string
  relativeTo?: 'event_start' | 'event_end'
  offsetMinutes?: number
}
export interface CampaignWrite {
  name?: string
  subject?: string
  bodySource?: string
  targetingRuleId?: string | null
}

export interface Webhook {
  id: string
  url: string
  events: string
  enabled: boolean
  description?: string | null
  lastStatus?: string | null
  lastDeliveredAt?: string | null
  secret?: string
}
export interface WebhooksResponse {
  eventTypes: string[]
  endpoints: Webhook[]
}
export interface ApiKeyInfo {
  id: string
  name: string
  prefix: string
  enabled: boolean
  lastUsedAt?: string | null
  createdAt?: string | null
}

export interface CheckinResult {
  id: string
  email: string
  fullName?: string | null
  company?: string | null
  checkedIn: boolean
  checkedInAt?: string | null
  alreadyCheckedIn?: boolean
}

export interface CheckinSummary {
  total: number
  checkedIn: number
  recent: CheckinResult[]
}

export interface AdminSponsor {
  id: string
  name: string
  tier?: string | null
  tierOrdinal: number
  ordinal: number
  logoUrl?: string | null
  websiteUrl?: string | null
  description?: string | null
  booth?: string | null
  leadCount?: number
}

export interface SponsorWrite {
  name?: string
  tier?: string | null
  tierOrdinal?: number
  ordinal?: number
  logoUrl?: string | null
  websiteUrl?: string | null
  description?: string | null
  booth?: string | null
}

export interface AdminPollOption {
  id: string
  label: string
  ordinal: number
  votes: number
}
export interface AdminPoll {
  id: string
  sessionIds: string[]
  sessionTitles?: string[]
  question: string
  status: 'draft' | 'open' | 'closed'
  ordinal: number
  totalVotes: number
  options: AdminPollOption[]
}
export interface AdminQuestion {
  id: string
  sessionId?: string | null
  sessionTitle?: string | null
  body: string
  authorName?: string | null
  status: string
  answered: boolean
  answer?: string | null
  answeredBy?: string | null
  answeredAt?: string | null
  upvotes: number
  createdAt?: string | null
}

export interface PageTemplate {
  id: string
  name: string
  description?: string | null
  scope: 'page' | 'section'
  isStarter: boolean
  createdAt?: string
}

// --- Registration + selectable options (admin) ---------------------------

export interface RegistrationField {
  id: string
  ordinal: number
  fieldType: string
  label: string
  required: boolean
  options?: string | null
  helperText?: string | null
}

export interface RegistrationFieldWrite {
  ordinal?: number
  fieldType?: string
  label?: string
  required?: boolean
  options?: string | null
  helperText?: string | null
}

export interface OptionAdmin {
  id: string
  optionGroupId: string
  title: string
  description?: string | null
  location?: string | null
  startsAt?: string | null
  endsAt?: string | null
  capacity?: number | null
  ordinal: number
  targetingRuleId?: string | null
}

export interface OptionGroupAdmin {
  id: string
  title: string
  description?: string | null
  ordinal: number
  minSelect: number
  maxSelect: number
  requiresRegistration: boolean
  selectionDeadline?: string | null
  targetingRuleId?: string | null
  options: OptionAdmin[]
}

export interface OptionGroupWrite {
  title?: string
  description?: string | null
  ordinal?: number
  minSelect?: number
  maxSelect?: number
  requiresRegistration?: boolean
  selectionDeadline?: string | null
  targetingRuleId?: string | null
}

export interface OptionWrite {
  title?: string
  description?: string | null
  location?: string | null
  startsAt?: string | null
  endsAt?: string | null
  capacity?: number | null
  ordinal?: number
  targetingRuleId?: string | null
}

export interface RegistrationRow {
  attendeeId: string
  email: string
  fullName?: string
  company?: string
  track?: string
  status: string
  registeredAt?: string
}

export interface PersonProfile {
  id: string
  email: string
  fullName?: string | null
  headline?: string | null
  bio?: string | null
  photoUrl?: string | null
  avatarKind?: string | null
  avatarPreset?: string | null
  avatarColor?: string | null
  initials?: string | null
  linkedinUrl?: string | null
  websiteUrl?: string | null
  aliases: { id: string; email: string }[]
  updatedAt?: string | null
}

export interface ApprovalRow {
  registrationId: string
  attendeeId: string
  email: string
  fullName?: string
  company?: string
  status: string
  source?: string
  values: Record<string, unknown>
  registeredAt?: string
  verifiedAt?: string
  approvedAt?: string
}

export interface RosterAttendee {
  attendeeId: string
  email: string
  fullName?: string
  company?: string
}

export interface RosterOption {
  optionId: string
  title: string
  capacity?: number | null
  count: number
  attendees: RosterAttendee[]
}

export interface GroupRoster {
  groupId: string
  title: string
  options: RosterOption[]
}

export interface TagPair {
  key: string
  value: string
}

export interface GuestTravel {
  hasTravel?: boolean
  arrivalFlight?: string | null
  arrivalDatetime?: string | null
  departureFlight?: string | null
  departureDatetime?: string | null
  airline?: string | null
  frequentFlyerNumber?: string | null
  knownTravelerNumber?: string | null
  seatPreference?: string | null
  hotelName?: string | null
  hotelAddress?: string | null
  hotelConfirmation?: string | null
  checkInDate?: string | null
  checkOutDate?: string | null
  carService?: string | null
  notes?: string | null
}

export interface AttendeeRow {
  id: string
  email: string
  fullName?: string
  company?: string
  attendeeType?: string
  track?: string
  externalRef?: string
  inviteStatus: string
  activatedAt?: string
  checkedInAt?: string | null
  tags: TagPair[]
}

export interface AttendeeUpdatePayload {
  fullName?: string
  company?: string
  attendeeType?: string
  track?: string
  externalRef?: string
  /** When provided, replaces the attendee's entire tag set. */
  tags?: TagPair[]
}

export interface AttendeeBulkPayload {
  attendeeIds: string[]
  /** Fields to overwrite on every selected attendee (blank = leave unchanged). */
  set?: { company?: string; attendeeType?: string; track?: string }
  /** Field names to wipe to blank: 'company' | 'attendeeType' | 'track'. */
  clear?: string[]
  addTags?: TagPair[]
  removeTags?: TagPair[]
}

export interface RosterColumn {
  key: string
  label: string
  group: 'attendee' | 'selections' | 'forms'
}

export interface AttendeeRoster {
  columns: RosterColumn[]
  rows: Record<string, string>[]
}

export interface NotificationRow {
  id: string
  title: string
  body?: string
  url?: string
  sentCount: number
  createdAt?: string
}

export interface AuditEntry {
  id: string
  actorName?: string
  actorSubject?: string
  action: string
  entityType: string
  entityId?: string
  eventId?: string
  summary?: string
  detail?: Record<string, unknown> | null
  occurredAt?: string
}

export interface TargetingCondition {
  key: string
  op: 'eq' | 'neq' | 'in'
  value: string | string[]
}

export interface TargetingRule {
  id: string
  name: string
  matchType: 'all' | 'any'
  includeAll: boolean
  conditions: TargetingCondition[]
}

export interface TargetingRuleWrite {
  name: string
  matchType: 'all' | 'any'
  includeAll: boolean
  conditions: TargetingCondition[]
}

export interface Analytics {
  funnel: { stage: string; count: number; pctOfTop: number }[]
  engagement: {
    pollVotes: number
    questions: number
    connections: number
    meetings: number
    sponsorLeads: number
    campaignSent: number
    campaignOpened: number
    campaignOpenRate: number
  }
  registrationTimeline: { date: string; count: number }[]
  selectionBreakdown: { group: string; options: { title: string; count: number }[] }[]
  range: { from: string | null; to: string | null }
  generatedAt: string
}

export interface AnalyticsStageAttendee {
  id: string
  email: string
  fullName: string | null
  company: string | null
  track: string | null
  inviteStatus: string | null
  checkedInAt: string | null
}

/** Optional inclusive date range for analytics queries (YYYY-MM-DD). */
export interface AnalyticsRange {
  from?: string
  to?: string
}

export interface EventReport {
  event: {
    id: string
    name: string
    status: string
    startsAt?: string
    endsAt?: string
  }
  content: {
    sessions: number
    contentSessions: number
    speakers: number
    featuredSpeakers: number
    contentItems: number
    forms: number
    tracks: string[]
  }
  agendaByType: { type: string; count: number }[]
  engagement: {
    totalResponses: number
    openForms: number
    uniqueRespondents: number
    forms: { id: string; title: string; status: string; responses: number }[]
  }
  activity: {
    invited: number
    activated: number
    activationRate: number
    activeAttendees: number
    appOpens: number
    agendaViews: number
    sessionViews: number
    contentViews: number
    contentDownloads: number
    formSubmits: number
    topSessions: { id: string; title: string; views: number }[]
    topContent: { id: string; title: string; hits: number }[]
  }
}

export interface FormResponseField {
  id: string
  label: string
  type: string
}

export interface FormResponseRow {
  id: string
  attendeeEmail?: string
  values: Record<string, unknown>
  submittedAt?: string
}

export interface FormResponsesResult {
  form: { id: string; title: string }
  fields: FormResponseField[]
  total: number
  responses: FormResponseRow[]
}
